import React, { useState, useEffect } from "react";
import { getFirestore, doc, updateDoc, onSnapshot, collection, query, where, limit, getDocs } from "firebase/firestore";
import app from "../../firebase";

export default function PreRegistrationProfileInfo({ studentId, setActive }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!studentId) return;
    const db = getFirestore(app);
    // listen by idNumber field (query) instead of assuming doc id == idNumber
    const q = query(collection(db, "students"), where("idNumber", "==", studentId), limit(1));

    // helper to load department years and set availableEntryLevels
    const loadDepartmentYears = async (deptName) => {
      if (!deptName) {
        setAvailableEntryLevels([]);
        return;
      }
      try {
        const dq = query(collection(db, "departments"), where("name", "==", deptName), limit(1));
        const dsnap = await getDocs(dq);
        if (!dsnap.empty) {
          const yrs = dsnap.docs[0].data().years || [];
          setAvailableEntryLevels(yrs.length ? yrs : []);
          // set default selection if current not in years
          setForm(prev => {
            if (yrs.length && (!prev.entryLevel || !yrs.includes(prev.entryLevel))) {
              return { ...prev, entryLevel: yrs[0] };
            }
            return prev;
          });
        } else {
          setAvailableEntryLevels([]);
        }
      } catch (err) {
        console.error('Failed to load department years', err);
        setAvailableEntryLevels([]);
      }
    };

    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const d = snap.docs[0].data();
        if (d.preRegistrationStatus === "submitted") setDone(true);
        // attempt to resolve department from student doc (try multiple fields)
        const deptName = d.department || d.program || d.school || "";
        loadDepartmentYears(deptName);
      } else {
        setDone(false);
      }
    });
    return () => unsub();
  }, [studentId]);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    entryLevel: '',
    semester: '',
    nationality: '',
    religion: '',
    civilStatus: '',
    birthPlace: '',
    present: {
      house: '',
      barangay: '',
      city: '',
      province: '',
      postal: '',
      country: '',
    },
    permanent: {
      house: '',
      barangay: '',
      city: '',
      province: '',
      postal: '',
      country: '',
    },
    sameAsPresent: false,
    elementary: { school: '', address: '', year: '' },
    junior: { school: '', address: '', year: '' },
    senior: { school: '', address: '', year: '', strand: '' },
    parentName: '',
    relationship: '',
    occupation: '',
    familyAddress: '',
    familySameAsPresent: false,
    familyMobile: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Dynamic entry level options based on department 'years' field in departments collection
  const [availableEntryLevels, setAvailableEntryLevels] = useState([]);
  const entryLevels = availableEntryLevels;
  const semesters = ['First Semester', 'Second Semester'];
  const civilStatuses = ['Single', 'Married', 'Widowed', 'Separated', 'Divorced'];
  const relationships = ['Father', 'Mother', 'Guardian', 'Other'];
  const currentYear = new Date().getFullYear();
  const isAlphaSpace = (value) => /^[A-Za-z\s]+$/.test(value.trim());
  const isValidYear = (value) => /^\d{4}$/.test(value) && Number(value) >= 1920 && Number(value) <= currentYear;
  const processAlphaText = (val) => val.toUpperCase().replace(/[^A-Z\s]/g, '').slice(0, 20);

  const validateProfile = () => {
    const newErrors = {};
    if (!form.entryLevel) newErrors.entryLevel = 'Entry Level is required';
    if (!form.semester) newErrors.semester = 'Semester is required';
    if (!form.nationality) newErrors.nationality = 'Nationality is required';
    else if (!isAlphaSpace(form.nationality)) newErrors.nationality = 'Invalid characters - letters only';
    if (!form.religion) newErrors.religion = 'Religion is required';
    else if (!isAlphaSpace(form.religion)) newErrors.religion = 'Invalid characters - letters only';
    if (!form.civilStatus) newErrors.civilStatus = 'Civil Status is required';
    if (!form.birthPlace) newErrors.birthPlace = 'Birth Place is required';
    else if (!isAlphaSpace(form.birthPlace)) newErrors.birthPlace = 'Invalid characters - letters only';
    const p = form.present;
    if (!p.house) newErrors.present_house = 'Required';
    if (!p.barangay) newErrors.present_barangay = 'Required';
    if (!p.city) newErrors.present_city = 'Required';
    else if (!isAlphaSpace(p.city)) newErrors.present_city = 'Invalid characters - letters only';
    if (!p.province) newErrors.present_province = 'Required';
    else if (!isAlphaSpace(p.province)) newErrors.present_province = 'Invalid characters - letters only';
    if (!p.postal) newErrors.present_postal = 'Required';
    if (!p.country) newErrors.present_country = 'Required';
    else if (!isAlphaSpace(p.country)) newErrors.present_country = 'Invalid characters - letters only';
    if (!form.sameAsPresent) {
      const pm = form.permanent;
      if (!pm.house) newErrors.permanent_house = 'Required';
      if (!pm.barangay) newErrors.permanent_barangay = 'Required';
      if (!pm.city) newErrors.permanent_city = 'Required';
      else if (!isAlphaSpace(pm.city)) newErrors.permanent_city = 'Invalid characters - letters only';
      if (!pm.province) newErrors.permanent_province = 'Required';
      else if (!isAlphaSpace(pm.province)) newErrors.permanent_province = 'Invalid characters - letters only';
      if (!pm.postal) newErrors.permanent_postal = 'Required';
      if (!pm.country) newErrors.permanent_country = 'Required';
      else if (!isAlphaSpace(pm.country)) newErrors.permanent_country = 'Invalid characters - letters only';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEducation = () => {
    const newErrors = {};
    if (!form.elementary.school) newErrors.elem_school = 'Required';
    else if (!isAlphaSpace(form.elementary.school)) newErrors.elem_school = 'Invalid characters - letters only';
    if (!form.elementary.address) newErrors.elem_address = 'Required';
    if (!form.elementary.year) newErrors.elem_year = 'Required';
    else if (!isValidYear(form.elementary.year)) newErrors.elem_year = 'Enter a valid year';
    if (!form.junior.school) newErrors.junior_school = 'Required';
    else if (!isAlphaSpace(form.junior.school)) newErrors.junior_school = 'Invalid characters - letters only';
    if (!form.junior.address) newErrors.junior_address = 'Required';
    if (!form.junior.year) newErrors.junior_year = 'Required';
    else if (!isValidYear(form.junior.year)) newErrors.junior_year = 'Enter a valid year';
    if (!form.senior.school) newErrors.senior_school = 'Required';
    else if (!isAlphaSpace(form.senior.school)) newErrors.senior_school = 'Invalid characters - letters only';
    if (!form.senior.address) newErrors.senior_address = 'Required';
    if (!form.senior.year) newErrors.senior_year = 'Required';
    else if (!isValidYear(form.senior.year)) newErrors.senior_year = 'Enter a valid year';
    if (!form.senior.strand) newErrors.senior_strand = 'Required';
    else if (!isAlphaSpace(form.senior.strand)) newErrors.senior_strand = 'Invalid characters - letters only';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateFamily = () => {
    const newErrors = {};
    if (!form.parentName) newErrors.parentName = 'Required';
    else if (!isAlphaSpace(form.parentName)) newErrors.parentName = 'Invalid characters - letters only';
    if (!form.relationship) newErrors.relationship = 'Required';
    if (!form.occupation) newErrors.occupation = 'Required';
    else if (!isAlphaSpace(form.occupation)) newErrors.occupation = 'Invalid characters - letters only';
    if (!form.familyAddress) newErrors.familyAddress = 'Required';
    if (!form.familyMobile) newErrors.familyMobile = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const processText = (val) => val.toUpperCase().slice(0, 20);
    const processAlphaText = (val) => val.toUpperCase().replace(/[^A-Z\s]/g, '').slice(0, 20);
    const processNumber = (val, maxLen) => val.replace(/[^0-9]/g, '').slice(0, maxLen);

    if (name.startsWith('present.')) {
      const key = name.split('.')[1];
      let newVal = value;
      if (["city","province","country"].includes(key)) newVal = processAlphaText(value);
      else if (["house","barangay"].includes(key)) newVal = processText(value);
      if (key === "postal") newVal = processNumber(value, 10);
      setForm((prev) => ({ ...prev, present: { ...prev.present, [key]: newVal } }));
    } else if (name.startsWith('permanent.')) {
      const key = name.split('.')[1];
      let newVal = value;
      if (["city","province","country"].includes(key)) newVal = processAlphaText(value);
      else if (["house","barangay"].includes(key)) newVal = processText(value);
      if (key === "postal") newVal = processNumber(value, 10);
      setForm((prev) => ({ ...prev, permanent: { ...prev.permanent, [key]: newVal } }));
    } else if (name === 'sameAsPresent') {
      setForm((prev) => ({
        ...prev,
        sameAsPresent: checked,
        permanent: checked ? { ...prev.present } : { house: '', barangay: '', city: '', province: '', postal: '', country: '' },
      }));
    } else if (name.startsWith('elementary.')) {
      const key = name.split('.')[1];
      let newVal = value;
      if (key === 'school') newVal = processAlphaText(value);
      else if (key === 'address') newVal = processText(value);
      if (key === "year") newVal = processNumber(value, 4);
      setForm((prev) => ({ ...prev, elementary: { ...prev.elementary, [key]: newVal } }));
    } else if (name.startsWith('junior.')) {
      const key = name.split('.')[1];
      let newVal = value;
      if (key === 'school') newVal = processAlphaText(value);
      else if (key === 'address') newVal = processText(value);
      if (key === "year") newVal = processNumber(value, 4);
      setForm((prev) => ({ ...prev, junior: { ...prev.junior, [key]: newVal } }));
    } else if (name.startsWith('senior.')) {
      const key = name.split('.')[1];
      let newVal = value;
      if (key === 'school') newVal = processAlphaText(value);
      else if (key === 'address') newVal = processText(value);
      else if (key === 'strand') newVal = processAlphaText(value);
      if (key === "year") newVal = processNumber(value, 4);
      setForm((prev) => ({ ...prev, senior: { ...prev.senior, [key]: newVal } }));
    } else if (name === 'familySameAsPresent') {
      setForm((prev) => ({
        ...prev,
        familySameAsPresent: checked,
        familyAddress: checked ? prev.present.house + ', ' + prev.present.barangay + ', ' + prev.present.city + ', ' + prev.present.province + ', ' + prev.present.postal + ', ' + prev.present.country : '',
      }));
    } else if (name === 'parentName' || name === 'occupation') {
      setForm((prev) => ({ ...prev, [name]: processAlphaText(value) }));
    } else if (name === 'familyMobile') {
      setForm((prev) => ({ ...prev, familyMobile: processNumber(value, 11) }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value.length > 20 ? value.slice(0, 20) : value }));
    }
  };

  const handleProfileNext = (e) => {
    e.preventDefault();
    if (!validateProfile()) return;
    setStep(2);
  };

  const handleEducationNext = (e) => {
    e.preventDefault();
    if (!validateEducation()) return;
    setStep(3);
  };

  const handleFamilyFinish = async (e) => {
    e.preventDefault();
    if (!validateFamily()) return;
    setSaving(true);
    try {
      if (!studentId) {
        alert('Student ID not found. Please reload the page.');
        setSaving(false);
        return;
      }
      const db = getFirestore(app);
      // resolve the actual document by idNumber field
      const q = query(collection(db, "students"), where("idNumber", "==", studentId), limit(1));
      const snap = await getDocs(q);
      if (snap.empty) {
        alert('Student record not found. Please contact support.');
        setSaving(false);
        return;
      }
      const studentDoc = snap.docs[0];
      const studentRef = doc(db, "students", studentDoc.id);

      await updateDoc(studentRef, {
        preRegistration: form,
        preRegistrationStatus: "submitted",
        preRegistrationSubmittedAt: new Date().toISOString(),
      });
      setSaving(false);
      setDone(true);
      setTimeout(() => {
        if (typeof setActive === 'function') {
          setActive('course-lineup');
        }
      }, 500);
    } catch (err) {
      console.error('Pre-registration save error:', err);
      setSaving(false);
      alert('Failed to save pre-registration. Please try again.');
    }
  };

  const handleBack = () => setStep(step - 1);

  const stepContainerStyle = { maxWidth: 1050, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 48 };
  if (done) {
    return (
      <div style={{ maxWidth: 700, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 48, textAlign: 'center' }}>
        <h2 style={{ color: '#155c2b', fontWeight: 700, marginBottom: 18 }}>Course lineup is available</h2>
        <div style={{ fontSize: 18, color: '#18803b', marginBottom: 24 }}>
          Course lineup is available. Please review your course line-up
        </div>
      </div>
    );
  }

  if (step === 1) {
    return (
      <div style={stepContainerStyle}>
        <h2 style={{ color: '#155c2b', fontWeight: 700, marginBottom: 18 }}>Profile Information</h2>
        <form onSubmit={handleProfileNext}>
          <div style={{ display: 'flex', gap: 18, marginBottom: 18 }}>
            <div style={{ flex: 1 }}>
              <label>Entry Level:<span style={{ color: 'red' }}>*</span></label>
              <select name="entryLevel" value={form.entryLevel} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }}>
                <option value="">Select Entry Level</option>
                {entryLevels.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              {errors.entryLevel && <div style={{ color: 'red', fontSize: 13 }}>{errors.entryLevel}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label>Semester:<span style={{ color: 'red' }}>*</span></label>
              <select name="semester" value={form.semester} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }}>
                <option value="">Select Semester</option>
                {semesters.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {errors.semester && <div style={{ color: 'red', fontSize: 13 }}>{errors.semester}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, marginBottom: 18 }}>
            <div style={{ flex: 1 }}>
              <label>Nationality:<span style={{ color: 'red' }}>*</span></label>
              <input name="nationality" value={form.nationality} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.nationality && <div style={{ color: 'red', fontSize: 13 }}>{errors.nationality}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label>Religion:<span style={{ color: 'red' }}>*</span></label>
              <input name="religion" value={form.religion} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.religion && <div style={{ color: 'red', fontSize: 13 }}>{errors.religion}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, marginBottom: 18 }}>
            <div style={{ flex: 1 }}>
              <label>Civil Status:<span style={{ color: 'red' }}>*</span></label>
              <select name="civilStatus" value={form.civilStatus} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }}>
                <option value="">Select Civil Status</option>
                {civilStatuses.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.civilStatus && <div style={{ color: 'red', fontSize: 13 }}>{errors.civilStatus}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label>Birth Place:<span style={{ color: 'red' }}>*</span></label>
              <input name="birthPlace" value={form.birthPlace} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.birthPlace && <div style={{ color: 'red', fontSize: 13 }}>{errors.birthPlace}</div>}
            </div>
          </div>
          <div style={{ fontWeight: 700, color: '#18803b', fontSize: 18, margin: '18px 0 8px 0', borderTop: '1px dashed #ccc', paddingTop: 12 }}>Present Address</div>
          <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label>House No./Street:<span style={{ color: 'red' }}>*</span></label>
              <input name="present.house" value={form.present.house} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.present_house && <div style={{ color: 'red', fontSize: 13 }}>{errors.present_house}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label>Barangay:<span style={{ color: 'red' }}>*</span></label>
              <input name="present.barangay" value={form.present.barangay} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.present_barangay && <div style={{ color: 'red', fontSize: 13 }}>{errors.present_barangay}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label>City:<span style={{ color: 'red' }}>*</span></label>
              <input name="present.city" value={form.present.city} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.present_city && <div style={{ color: 'red', fontSize: 13 }}>{errors.present_city}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label>Province:<span style={{ color: 'red' }}>*</span></label>
              <input name="present.province" value={form.present.province} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.present_province && <div style={{ color: 'red', fontSize: 13 }}>{errors.present_province}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label>Postal Code:<span style={{ color: 'red' }}>*</span></label>
              <input name="present.postal" value={form.present.postal} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} maxLength={10} required />
              {errors.present_postal && <div style={{ color: 'red', fontSize: 13 }}>{errors.present_postal}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label>Country:<span style={{ color: 'red' }}>*</span></label>
              <input name="present.country" value={form.present.country} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.present_country && <div style={{ color: 'red', fontSize: 13 }}>{errors.present_country}</div>}
            </div>
          </div>
          <div style={{ fontWeight: 700, color: '#18803b', fontSize: 18, margin: '18px 0 8px 0', borderTop: '1px dashed #ccc', paddingTop: 12 }}>Permanent Address</div>
          <div style={{ marginBottom: 12 }}> 
            <label style={{ fontWeight: 400, fontSize: 15 }}>
              <input type="checkbox" name="sameAsPresent" checked={form.sameAsPresent} onChange={handleChange} style={{ marginRight: 8 }} />
              Same as Present Address
            </label>
          </div>
          {!form.sameAsPresent && (
            <>
              <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label>House No./Street:<span style={{ color: 'red' }}>*</span></label>
                  <input name="permanent.house" value={form.permanent.house} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
                  {errors.permanent_house && <div style={{ color: 'red', fontSize: 13 }}>{errors.permanent_house}</div>}
                </div>
                <div style={{ flex: 1 }}>
                  <label>Barangay:<span style={{ color: 'red' }}>*</span></label>
                  <input name="permanent.barangay" value={form.permanent.barangay} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
                  {errors.permanent_barangay && <div style={{ color: 'red', fontSize: 13 }}>{errors.permanent_barangay}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label>City:<span style={{ color: 'red' }}>*</span></label>
                  <input name="permanent.city" value={form.permanent.city} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
                  {errors.permanent_city && <div style={{ color: 'red', fontSize: 13 }}>{errors.permanent_city}</div>}
                </div>
                <div style={{ flex: 1 }}>
                  <label>Province:<span style={{ color: 'red' }}>*</span></label>
                  <input name="permanent.province" value={form.permanent.province} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
                  {errors.permanent_province && <div style={{ color: 'red', fontSize: 13 }}>{errors.permanent_province}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label>Postal Code:<span style={{ color: 'red' }}>*</span></label>
                  <input name="permanent.postal" value={form.permanent.postal} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} maxLength={10} required />
                  {errors.permanent_postal && <div style={{ color: 'red', fontSize: 13 }}>{errors.permanent_postal}</div>}
                </div>
                <div style={{ flex: 1 }}>
                  <label>Country:<span style={{ color: 'red' }}>*</span></label>
                  <input name="permanent.country" value={form.permanent.country} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
                  {errors.permanent_country && <div style={{ color: 'red', fontSize: 13 }}>{errors.permanent_country}</div>}
                </div>
              </div>
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 30 }}>
            <button type="submit" style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 32px', fontWeight: 600, fontSize: 16, letterSpacing: 1, boxShadow: '0 2px 8px rgba(39,174,96,0.07)', cursor: 'pointer' }}>Next</button>
          </div>
        </form>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div style={stepContainerStyle}>
        <h2 style={{ color: '#155c2b', fontWeight: 700, marginBottom: 18 }}>Education Information</h2>
        <form onSubmit={handleEducationNext}>
          <div style={{ fontWeight: 700, color: '#18803b', fontSize: 18, margin: '18px 0 8px 0', borderTop: '1px dashed #ccc', paddingTop: 12 }}>Elementary</div>
          <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label>Name of School:<span style={{ color: 'red' }}>*</span></label>
              <input name="elementary.school" value={form.elementary.school} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.elem_school && <div style={{ color: 'red', fontSize: 13 }}>{errors.elem_school}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label>Address:<span style={{ color: 'red' }}>*</span></label>
              <input name="elementary.address" value={form.elementary.address} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.elem_address && <div style={{ color: 'red', fontSize: 13 }}>{errors.elem_address}</div>}
            </div>
          </div>

          <div style={{ width: '50%', marginBottom: 18 }}>
            <label>Year of Graduation:<span style={{ color: 'red' }}>*</span></label>
            <input name="elementary.year" value={form.elementary.year} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} maxLength={4} pattern="[0-9]*" inputMode="numeric" required />
            {errors.elem_year && <div style={{ color: 'red', fontSize: 13 }}>{errors.elem_year}</div>}
          </div>

          <div style={{ fontWeight: 700, color: '#18803b', fontSize: 18, margin: '18px 0 8px 0', borderTop: '1px dashed #ccc', paddingTop: 12 }}>Junior High School</div>
          <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label>Name of School:<span style={{ color: 'red' }}>*</span></label>
              <input name="junior.school" value={form.junior.school} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.junior_school && <div style={{ color: 'red', fontSize: 13 }}>{errors.junior_school}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label>Address:<span style={{ color: 'red' }}>*</span></label>
              <input name="junior.address" value={form.junior.address} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.junior_address && <div style={{ color: 'red', fontSize: 13 }}>{errors.junior_address}</div>}
            </div>
          </div>

          <div style={{ width: '50%', marginBottom: 18 }}>
            <label>Year of Graduation:<span style={{ color: 'red' }}>*</span></label>
            <input name="junior.year" value={form.junior.year} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} maxLength={4} pattern="[0-9]*" inputMode="numeric" required />
            {errors.junior_year && <div style={{ color: 'red', fontSize: 13 }}>{errors.junior_year}</div>}
          </div>

          <div style={{ fontWeight: 700, color: '#18803b', fontSize: 18, margin: '18px 0 8px 0', borderTop: '1px dashed #ccc', paddingTop: 12 }}>Senior High School</div>
          <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label>Name of School:<span style={{ color: 'red' }}>*</span></label>
              <input name="senior.school" value={form.senior.school} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.senior_school && <div style={{ color: 'red', fontSize: 13 }}>{errors.senior_school}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label>Address:<span style={{ color: 'red' }}>*</span></label>
              <input name="senior.address" value={form.senior.address} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.senior_address && <div style={{ color: 'red', fontSize: 13 }}>{errors.senior_address}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, marginBottom: 18 }}>
            <div style={{ width: '50%' }}>
              <label>Year of Graduation:<span style={{ color: 'red' }}>*</span></label>
              <input name="senior.year" value={form.senior.year} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} maxLength={4} pattern="[0-9]*" inputMode="numeric" required />
              {errors.senior_year && <div style={{ color: 'red', fontSize: 13 }}>{errors.senior_year}</div>}
            </div>
            <div style={{ width: '50%' }}>
              <label>Strand:<span style={{ color: 'red' }}>*</span></label>
              <input name="senior.strand" value={form.senior.strand} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={2} maxLength={20} required />
              {errors.senior_strand && <div style={{ color: 'red', fontSize: 13 }}>{errors.senior_strand}</div>}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 30 }}>
            <button type="button" onClick={handleBack} style={{ background: '#888', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 32px', fontWeight: 600, fontSize: 16, letterSpacing: 1, cursor: 'pointer' }}>Back</button>
            <button type="submit" style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 32px', fontWeight: 600, fontSize: 16, letterSpacing: 1, boxShadow: '0 2px 8px rgba(39,174,96,0.07)', cursor: 'pointer' }}>Next</button>
          </div>
        </form>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div style={stepContainerStyle}>
        <h2 style={{ color: '#155c2b', fontWeight: 700, marginBottom: 18 }}>Family Information</h2>
        <form onSubmit={handleFamilyFinish}>
          <div style={{ display: 'flex', gap: 18, marginBottom: 18 }}>
            <div style={{ flex: 1 }}>
              <label>Name of Parent:<span style={{ color: 'red' }}>*</span></label>
              <input name="parentName" value={form.parentName} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.parentName && <div style={{ color: 'red', fontSize: 13 }}>{errors.parentName}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label>Relationship:<span style={{ color: 'red' }}>*</span></label>
              <select name="relationship" value={form.relationship} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }}>
                <option value="">Select Relationship</option>
                {relationships.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {errors.relationship && <div style={{ color: 'red', fontSize: 13 }}>{errors.relationship}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, marginBottom: 18 }}>
            <div style={{ flex: 1 }}>
              <label>Occupation:<span style={{ color: 'red' }}>*</span></label>
              <input name="occupation" value={form.occupation} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={20} required />
              {errors.occupation && <div style={{ color: 'red', fontSize: 13 }}>{errors.occupation}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label>Mobile No.:<span style={{ color: 'red' }}>*</span></label>
              <input name="familyMobile" value={form.familyMobile} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} maxLength={11} pattern="[0-9]*" inputMode="numeric" required />
              {errors.familyMobile && <div style={{ color: 'red', fontSize: 13 }}>{errors.familyMobile}</div>}
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <label>Address:<span style={{ color: 'red' }}>*</span></label>
            <input name="familyAddress" value={form.familyAddress} onChange={handleChange} style={{ width: '100%', padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} minLength={3} maxLength={50} required />
            {errors.familyAddress && <div style={{ color: 'red', fontSize: 13 }}>{errors.familyAddress}</div>}
            <div style={{ marginTop: 6 }}>
              <label style={{ fontWeight: 400, fontSize: 15 }}>
                <input type="checkbox" name="familySameAsPresent" checked={form.familySameAsPresent} onChange={handleChange} style={{ marginRight: 8 }} />
                Same as Present Address
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 30 }}>
            <button type="button" onClick={handleBack} style={{ background: '#888', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 32px', fontWeight: 600, fontSize: 16, letterSpacing: 1, cursor: 'pointer' }}>Back</button>
            <button type="submit" style={{ background: '#18803b', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 32px', fontWeight: 600, fontSize: 16, letterSpacing: 1, boxShadow: '0 2px 8px rgba(39,174,96,0.07)', cursor: 'pointer' }}>Finish</button>
          </div>
        </form>
      </div>
    );
  }
}